import { TextComponent, Tween, Viewer, ViewerModule, WindowComponent } from '@verybigthings/vbt-webvr-framework';
import { SpectrophotometerMode, SpectroPhotometerModeNames } from './index';
import { Color, Mesh, MeshBasicMaterial, PlaneBufferGeometry, SphereBufferGeometry, Vector2, Vector3 } from 'three';
const SPECTROPHOTOMETER_INDICATOR_DISABLED = 0x440000;
const SPECTROPHOTOMETER_INDICATOR_ENABLED = 0xEE0000;
export class SpectrophotometerScreen extends ViewerModule {
    constructor() {
        super();
        this._waveLength = 400;
        this._transmittance = -5;
        this._absorbance = 10;
        this._currentMode = SpectrophotometerMode.absorbance;
    }
    init() {
        this.generateUI();
        this.setCurrentMode(this._currentMode);
        this.setWaveLength(this._waveLength);
        this.setAbsorbance(this._absorbance);
        this.setTransmittance(this._transmittance);
    }
    getWaveLength() {
        return this._waveLength;
    }
    setWaveLength(value) {
        this._waveLength = value;
        const currWaveLength = +this.waveLengthTextLabel.options.text.value;
        this.waveLengthTextLabel.setText('' + this._waveLength);
        const waveRoundModel = Viewer.scene.getObjectByName('knob1');
        if (waveRoundModel) {
            this.rotateRoundTween && this.rotateRoundTween.stop();
            this.rotateRoundTween = new Tween({ alpha: 0 })
                .to({ alpha: 1 }, 280)
                .easing(TWEEN.Easing.Linear.None)
                .onUpdate(() => {
                waveRoundModel.rotation.z += (value - currWaveLength > 0) ? -0.025 : 0.025;
            })
                .start();
        }
    }
    getAbsorbance() {
        return this._absorbance;
    }
    setAbsorbance(value) {
        this._absorbance = value;
        const currAbsorbVal = +this.dataTextLabel.options.text.value;
        this.dataTextLabel.setText('' + this._absorbance);
        const absorbRoundModel = Viewer.scene.getObjectByName('knob2');
        if (absorbRoundModel) {
            this.rotateRoundTween && this.rotateRoundTween.stop();
            this.rotateRoundTween = new Tween({ alpha: 0 })
                .to({ alpha: 1 }, 280)
                .easing(TWEEN.Easing.Linear.None)
                .onUpdate(() => {
                absorbRoundModel.rotation.z += (value - currAbsorbVal > 0) ? -0.025 : 0.025;
            })
                .start();
        }
    }
    getTransmittance() {
        return this._transmittance;
    }
    setTransmittance(value, typeRound) {
        this._transmittance = value;
        const currTransmitVal = +this.dataTextLabel.options.text.value;
        this.dataTextLabel.setText('' + this._transmittance);
        if (!typeRound)
            return;
        if (Math.abs(value - currTransmitVal) > 10)
            return;
        const transmitRoundModel = Viewer.scene.getObjectByName(typeRound === 'zeroAdjustKnob' ? 'knob2' : 'knob3');
        if (transmitRoundModel) {
            this.rotateRoundTween && this.rotateRoundTween.stop();
            this.rotateRoundTween = new Tween({ alpha: 0 })
                .to({ alpha: 1 }, 280)
                .easing(TWEEN.Easing.Linear.None)
                .onUpdate(() => {
                transmitRoundModel.rotation.z += (value - currTransmitVal > 0) ? -0.025 : 0.025;
            })
                .start();
        }
    }
    getCurrentMode() {
        return this._currentMode;
    }
    setCurrentMode(value) {
        this._currentMode = value;
        this.modeIndicators.forEach((indicator, i) => indicator.material.color.setHex(i === value ? SPECTROPHOTOMETER_INDICATOR_ENABLED : SPECTROPHOTOMETER_INDICATOR_DISABLED));
        if (this._currentMode === SpectrophotometerMode.absorbance)
            this.dataTextLabel.setText('' + this._absorbance);
        else
            this.dataTextLabel.setText('' + this._transmittance);
    }
    generateUI() {
        let background, screenBorder, waveLengthNameLabel, dataNameLabel;
        // Adding main screen black background
        this.addObject(background = new Mesh(new PlaneBufferGeometry(14.5, 4), new MeshBasicMaterial({ color: 0x222222 })));
        // Adding screen white border
        this.addObject(screenBorder = new WindowComponent({
            size: new Vector2(14, 3),
            background: 0x222222,
            bordColor: 0xeeeeee,
            bordWidth: 0.1,
            bordRadius: 0.3
        }));
        screenBorder.uiObject.position.z = 0.05;
        screenBorder.uiObjectOuter.position.z -= 0.03;
        // Adding screen name labels
        this.addObject(waveLengthNameLabel = new TextComponent({
            size: new Vector2(1.02, 1.02 / 3),
            pxSize: new Vector2(64, 64 / 3),
            background: { color: '#222222' },
            text: {
                value: 'WAVELENGTH',
                style: { color: '#EEEEEE' },
                lineHeight: 30,
                align: 'center',
                autoWrappingHorizontal: true
            }
        }));
        waveLengthNameLabel.uiObject.position.set(-3.8134129800010506, 1.6, 0.1);
        this.addObject(dataNameLabel = new TextComponent({
            size: new Vector2(1.02, 1.02 / 3),
            pxSize: new Vector2(64, 64 / 3),
            background: { color: '#222222' },
            text: {
                value: 'DATA',
                style: { color: '#EEEEEE' },
                lineHeight: 30,
                align: 'center',
                autoWrappingHorizontal: true
            }
        }));
        dataNameLabel.uiObject.position.set(-0.3, 1.6, 0.1);
        const modeNamesLabels = SpectroPhotometerModeNames.map(name => {
            const label = new TextComponent({
                size: new Vector2(1.8 * 2.7, 0.3 * 2.7),
                pxSize: new Vector2(300, 50).multiplyScalar(window['quality'] === 'very low' ? 0.5 : 1),
                background: { color: '#222222' },
                text: {
                    value: name,
                    style: { size: `${34 * (window['quality'] === 'very low' ? 0.5 : 1)}px`, color: '#EEEEEE' },
                    align: 'left',
                    lineHeight: 37 * (window['quality'] === 'very low' ? 0.5 : 1),
                    autoWrappingHorizontal: false
                }
            });
            this.addObject(label);
            return label;
        });
        ViewerModule.adjustObjects(modeNamesLabels.map(label => label.uiObject), true, 0.55, 0);
        modeNamesLabels.forEach(label => label.uiObject.position.add(new Vector3(4.46, 0.1, 0.1)));
        // Adding indicators
        this.modeIndicators = modeNamesLabels.map(name => {
            const indicator = new Mesh(new SphereBufferGeometry(0.15), new MeshBasicMaterial({
                color: SPECTROPHOTOMETER_INDICATOR_DISABLED
            }));
            this.addObject(indicator);
            return indicator;
        });
        ViewerModule.adjustObjects(this.modeIndicators, true, 1.0, 0);
        this.modeIndicators.forEach(indicator => indicator.position.add(new Vector3(1.6, -0.02, 0.1)));
        // window['vm1'] = this;
        // Adding screen value labels
        this.addObject(this.waveLengthTextLabel = new TextComponent({
            size: new Vector2(2, 2),
            pxSize: new Vector2(64, 64),
            background: { color: '#222222' },
            text: {
                value: '000',
                style: { color: new Color(SPECTROPHOTOMETER_INDICATOR_ENABLED).getStyle() },
                align: 'center',
                autoWrappingHorizontal: true
            }
        }));
        this.waveLengthTextLabel.uiObject.position.set(-3.868475899369959, 0.14371117084064577, 0.1);
        this.addObject(this.dataTextLabel = new TextComponent({
            size: new Vector2(2, 2),
            pxSize: new Vector2(64, 64),
            background: { color: '#222222' },
            text: {
                value: '000',
                style: { color: new Color(SPECTROPHOTOMETER_INDICATOR_ENABLED).getStyle() },
                align: 'center',
                autoWrappingHorizontal: true
            }
        }));
        this.dataTextLabel.uiObject.position.set(-0.2967310708328057, 0.14371117084064577, 0.1);
    }
}
